import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  ErrorModal,
  InputTextVertical,
  SelectControllerVertical,
  SuccessModal,
  VerticalInputFormLayout,
} from "@components";

import { useMetaData } from "@hooks/inventory/settings/useMetaData";

import useActionCreator from "../../../../../../redux/hooks/useActionCreator";

import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";

import { SETTINGS_META_DATA } from "@constants/inventory/settings-meta-data";
import {
  settingsNameValidationSchema,
  SETTINGS_NAME,
} from "@validations/Inventory/settings/settingsNameValidation";

import { NameCreateType } from "@type/inventory/name";
import { NameState } from "@reducers/types/inventory/name-state";

const { GROUP, BRAND } = SETTINGS_META_DATA;

export default function NameForm({
  settingsNameState,
}: {
  settingsNameState: NameState; // CategoryState
}) {
  const [productId, setProductId] = useState("");
  const {
    getSingleData,
    createSuccess,
    deleteSuccess,
    createError,
    deleteError,
    updateError,
    updateSuccess,
  } = settingsNameState;
  const { data } = getSingleData || {};
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NameCreateType>({
    resolver: yupResolver(settingsNameValidationSchema),
    values: data,
  });

  const { data: groups } = useMetaData(GROUP);
  const { data: brands } = useMetaData(BRAND);

  const { nameCreateActionHandler, nameUpdateActionHandler } =
    useActionCreator();

  const onSubmit: SubmitHandler<NameCreateType> = (data) => {
    if (productId) {
      nameUpdateActionHandler(productId, data);
    } else {
      nameCreateActionHandler(data);
    }
  };

  const [successSubtitle, setSuccessSubtitle] = useState<string>("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const createSuccessAction = () => {
    reset();
    setIsSuccessModalOpen(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  useEffect(() => {
    if (createSuccess) {
      setSuccessSubtitle("You have created an inventory settings name");
      setIsSuccessModalOpen(true);
    }

    if (deleteSuccess) {
      setSuccessSubtitle("You have deleted an inventory settings name");
      setIsSuccessModalOpen(true);
    }

    if (updateSuccess) {
      setSuccessSubtitle("You have updated an inventory settings category");
      setIsSuccessModalOpen(true);
      setProductId("");
      reset();
    }

    if (createError || deleteError || updateError) {
      setSuccessSubtitle("");
      setIsErrorModalOpen(true);
    }
  }, [
    createSuccess,
    deleteSuccess,
    createError,
    deleteError,
    updateError,
    updateSuccess,
  ]);

  const resetAction = () => {
    reset();
    setProductId("");
  };

  useEffect(() => {
    if (data) {
      setValue(SETTINGS_NAME.GROUP, data?.group?.id);
      setValue(SETTINGS_NAME.BRAND, data?.brand?.id);
      setProductId(data.id);
    }
  }, [data, setValue]);

  useEffect(() => resetAction(), []);

  return (
    <div>
      <VerticalInputFormLayout
        label="INVENTORY_SETTINGS.NAME.FORM.HEADER"
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isUpdate={!!productId}
        loading={
          settingsNameState.isCreateLoading ||
          settingsNameState.isUpdateLoading ||
          settingsNameState.isGetSingleLoading ||
          settingsNameState.isDeleteLoading
        }
        cancelAction={resetAction}
      >
        <SelectControllerVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.GROUP"
          name="groupId"
          register={{
            ...register(SETTINGS_NAME.GROUP),
          }}
          options={getConvertedMetaData(groups)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
        />

        <SelectControllerVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.BRAND"
          name="brandId"
          register={{
            ...register(SETTINGS_NAME.BRAND),
          }}
          options={getConvertedMetaData(brands)}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
        />

        <InputTextVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.NAME_BN"
          register={{
            ...register(SETTINGS_NAME.NAME_BN),
          }}
          error={errors}
          required
          cols={3}
        />

        <InputTextVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.NAME_EN"
          register={{
            ...register(SETTINGS_NAME.NAME_EN),
          }}
          error={errors}
          required
          cols={3}
        />

        <SelectControllerVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.TYPE"
          name="type"
          register={{
            ...register(SETTINGS_NAME.TYPE),
          }}
          options={[
            {
              label: "Item",
              value: "item",
            },
            {
              label: "Asset",
              value: "asset",
            },
          ]}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
        />

        <SelectControllerVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.INVENTORY_TYPE"
          name="inventoryType"
          register={{
            ...register(SETTINGS_NAME.INVENTORY_TYPE),
          }}
          options={[
            {
              label: "ICT",
              value: "ICT",
            },
            {
              label: "Non ICT",
              value: "NON_ICT",
            },
          ]}
          setValue={setValue}
          control={control}
          error={errors}
          required
          cols={3}
        />
        <InputTextVertical
          label="INVENTORY_SETTINGS.NAME.FORM.FIELDS.DESCRIPTION"
          register={{
            ...register(SETTINGS_NAME.DESCRIPTION),
          }}
          error={errors}
          cols={3}
        />
      </VerticalInputFormLayout>

      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle={successSubtitle}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </div>
  );
}
